import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDownIcon, ChevronUpIcon, ArrowPathIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import Modal from '../../ui/Modal';
import { EDITABLE_PROMPTS, AVAILABLE_MODELS, DEFAULT_PROMPTS, getDefaultModel, loadReportConfig } from '../../../config/reportPrompts';
import { validateAllPrompts, validateModel, formatValidationErrors } from '../../../utils/promptValidation';

/**
 * ReportConfigModal Component
 * 
 * Modal for configuring report generation with custom prompts and model selection
 * 
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Function to close modal
 * @param {function} onSubmit - Function called with (customPrompts, llmModel) on submit
 * @param {Object} defaultPrompts - Optional default prompts object
 * @param {string} initialModel - Optional initial model value
 * @param {Object} initialCustomPrompts - Optional initial custom prompts
 */
const ReportConfigModal = ({
  isOpen,
  onClose,
  onSubmit,
  defaultPrompts = {},
  initialModel = null,
  initialCustomPrompts = {}
}) => {
  const [selectedModel, setSelectedModel] = useState(initialModel || getDefaultModel());
  const [customPrompts, setCustomPrompts] = useState({});
  const [enabledPrompts, setEnabledPrompts] = useState({});
  const [expandedPrompts, setExpandedPrompts] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [config, setConfig] = useState(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const configLoadedRef = useRef(false); // Track if config was loaded to prevent infinite loop
  const initializedRef = useRef(false); // Track if initial values were set to prevent infinite loop
  const userPickedModelRef = useRef(false); // Track if user manually selected a model

  // Load configuration from backend when modal opens
  useEffect(() => {
    // Reset flags when modal closes
    if (!isOpen) {
      configLoadedRef.current = false;
      initializedRef.current = false;
      return;
    }

    // Only load config once when modal opens
    if (isOpen && !configLoadedRef.current) {
      configLoadedRef.current = true;
      setIsLoadingConfig(true);
      loadReportConfig()
        .then(loadedConfig => {
          setConfig(loadedConfig);
          setIsLoadingConfig(false);
        })
        .catch(error => {
          console.error('Failed to load report config:', error);
          setIsLoadingConfig(false);
          // Use fallback values - DEFAULT_PROMPTS is only used if API fails
          setConfig({
            editablePrompts: EDITABLE_PROMPTS,
            availableModels: AVAILABLE_MODELS,
            defaultPrompts: DEFAULT_PROMPTS
          });
        });
    }
  }, [isOpen]); // Only depend on isOpen to prevent infinite loop

  // Initialize with initial values - only once when modal opens
  useEffect(() => {
    if (isOpen && !initializedRef.current) {
      initializedRef.current = true;
      userPickedModelRef.current = false;
      setSelectedModel(initialModel || getDefaultModel());
      setCustomPrompts(initialCustomPrompts || {});
      setEnabledPrompts(
        Object.keys(initialCustomPrompts || {}).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {})
      );
      setValidationErrors({});
      setSubmitError(null);
    }
  }, [isOpen]); // Only depend on isOpen to prevent infinite loop

  // Sync selectedModel with backend's declared default once the config loads.
  // The local fallback `getDefaultModel()` may not match what the backend reports
  // as the default model; without this sync, the radio button visually highlights
  // the local default while the backend may pick a different one for null requests.
  useEffect(() => {
    if (!isOpen || !config || initialModel || userPickedModelRef.current) return;
    const models = config.availableModels || [];
    const backendDefault = models.find(m => m.isDefault || m.is_default);
    if (backendDefault && backendDefault.value !== selectedModel) {
      setSelectedModel(backendDefault.value);
    }
  }, [isOpen, config, initialModel]); // intentionally not depending on selectedModel

  // Use loaded config or fallback - memoized to prevent unnecessary recalculations
  const editablePrompts = useMemo(() => {
    return config?.editablePrompts || EDITABLE_PROMPTS;
  }, [config?.editablePrompts]);

  const availableModels = useMemo(() => {
    return config?.availableModels || AVAILABLE_MODELS;
  }, [config?.availableModels]);

  const defaultPromptsToUse = useMemo(() => {
    return config?.defaultPrompts || DEFAULT_PROMPTS;
  }, [config?.defaultPrompts]);

  // Validate prompt when it changes
  const validatePrompt = (promptKey, promptText) => {
    const promptConfig = editablePrompts.find(p => p.key === promptKey);
    if (!promptConfig) return;

    const errors = [];
    
    if (!promptText || !promptText.trim()) {
      errors.push('Prompt text cannot be empty');
    } else {
      // Check for missing placeholders
      const requiredPlaceholders = promptConfig.requiredPlaceholders || promptConfig.required_placeholders || [];
      const missingPlaceholders = requiredPlaceholders.filter(
        placeholder => !promptText.includes(placeholder)
      );
      if (missingPlaceholders.length > 0) {
        errors.push(`Missing required placeholders: ${missingPlaceholders.join(', ')}`);
      }
    }

    if (errors.length > 0) {
      setValidationErrors(prev => ({ ...prev, [promptKey]: errors }));
    } else {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[promptKey];
        return newErrors;
      });
    }
  };

  const handlePromptToggle = (promptKey) => {
    const isEnabled = enabledPrompts[promptKey] || false;
    setEnabledPrompts(prev => ({
      ...prev,
      [promptKey]: !isEnabled
    }));

    if (!isEnabled) {
      // Enable prompt - initialize with default if not already set
      if (!customPrompts[promptKey] && defaultPrompts[promptKey]) {
        setCustomPrompts(prev => ({
          ...prev,
          [promptKey]: defaultPrompts[promptKey]
        }));
        validatePrompt(promptKey, defaultPrompts[promptKey]);
      }
    } else {
      // Disable prompt - remove from custom prompts
      setCustomPrompts(prev => {
        const newPrompts = { ...prev };
        delete newPrompts[promptKey];
        return newPrompts;
      });
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[promptKey];
        return newErrors;
      });
    }
  };

  const handlePromptChange = (promptKey, value) => {
    setCustomPrompts(prev => ({
      ...prev,
      [promptKey]: value
    }));
    validatePrompt(promptKey, value);
  };

  const handleResetPrompt = (promptKey) => {
    if (defaultPrompts[promptKey]) {
      setCustomPrompts(prev => ({
        ...prev,
        [promptKey]: defaultPrompts[promptKey]
      }));
      validatePrompt(promptKey, defaultPrompts[promptKey]);
    }
  };

  const handleResetAll = () => {
    setCustomPrompts({});
    setEnabledPrompts({});
    setValidationErrors({});
    setSelectedModel(getDefaultModel());
  };

  const togglePromptExpanded = (promptKey) => {
    setExpandedPrompts(prev => ({
      ...prev,
      [promptKey]: !prev[promptKey]
    }));
  };

  const handleSubmit = async () => {
    setIsValidating(true);
    setSubmitError(null);
    
    // Validate model
    const modelValidation = validateModel(selectedModel, availableModels);
    if (!modelValidation.isValid) {
      setSubmitError(modelValidation.error);
      setIsValidating(false);
      return;
    }

    // Validate all enabled prompts
    const enabledCustomPrompts = Object.keys(enabledPrompts)
      .filter(key => enabledPrompts[key])
      .reduce((acc, key) => {
        if (customPrompts[key]) {
          acc[key] = customPrompts[key];
        }
        return acc;
      }, {});

    const validation = validateAllPrompts(enabledCustomPrompts, editablePrompts);
    
    if (!validation.isValid) {
      const errorMsg = formatValidationErrors(validation.errors, editablePrompts);
      setSubmitError(`Please fix the following errors:\n${errorMsg}`);
      setValidationErrors(validation.errors);
      setIsValidating(false);
      return;
    }

    setIsValidating(false);
    
    // Call onSubmit with configuration.
    // Always send the explicitly selected model so the backend uses exactly what
    // the user picked, regardless of any frontend/backend default mismatch.
    const config = {
      llm_model: selectedModel || null,
      custom_prompts: Object.keys(enabledCustomPrompts).length > 0 ? enabledCustomPrompts : null
    };
    
    // onSubmit is now async and will close the modal when done
    await onSubmit(config.custom_prompts, config.llm_model);
  };

  // Check if form is valid
  const isFormValid = () => {
    const hasErrors = Object.keys(validationErrors).length > 0;
    const modelValidation = validateModel(selectedModel, availableModels);
    return !hasErrors && modelValidation.isValid;
  };

  // Get default prompt text (placeholder if not available)
  const getDefaultPromptText = (promptKey) => {
    return defaultPromptsToUse[promptKey] || `[Default prompt for ${promptKey}]\n\nThis prompt will use the system default.`;
  };

  // Show loading state while config is loading
  if (isLoadingConfig) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Configure Report Generation" size="xl">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading configuration...</p>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Configure Report Generation" size="xl">
      <div className="space-y-6 max-h-[70vh] overflow-y-auto">
        {/* Model Selection Section */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-900">AI Model</h4>
          <div className="space-y-2">
            {availableModels.map((model) => (
              <label
                key={model.value}
                className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedModel === model.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="llm_model"
                  value={model.value}
                  checked={selectedModel === model.value}
                  onChange={(e) => {
                    userPickedModelRef.current = true;
                    setSelectedModel(e.target.value);
                  }}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{model.label}</span>
                    {(model.isDefault || model.is_default) && (
                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                        Default
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{model.provider}</div>
                  <div className="text-sm text-gray-600 mt-1">{model.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Custom Prompts Section */}
        <div className="space-y-3 border-t pt-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-900">Custom Prompts</h4>
            <button
              onClick={handleResetAll}
              className="text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Reset All
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Customize prompts to change how the report is generated. Make sure to include all required placeholders.
          </p>

          <div className="space-y-2">
            {editablePrompts.map((promptConfig) => {
              const isEnabled = enabledPrompts[promptConfig.key] || false;
              const isExpanded = expandedPrompts[promptConfig.key] || false;
              const promptText = customPrompts[promptConfig.key] || getDefaultPromptText(promptConfig.key);
              const errors = validationErrors[promptConfig.key] || [];

              return (
                <div
                  key={promptConfig.key}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  {/* Prompt Header - Clickable for expand/collapse */}
                  <div 
                    className="bg-gray-50 px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => togglePromptExpanded(promptConfig.key)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-sm text-gray-900">
                          {promptConfig.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {promptConfig.description}
                        </div>
                      </div>
                      <div className="text-gray-400 ml-4">
                        {isExpanded ? (
                          <ChevronUpIcon className="h-5 w-5" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5" />
                        )}
                      </div>
                    </div>

                    {/* Required Placeholders */}
                    {(promptConfig.requiredPlaceholders || promptConfig.required_placeholders) && 
                     (promptConfig.requiredPlaceholders || promptConfig.required_placeholders).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        <span className="text-xs text-gray-500">Required placeholders:</span>
                        {(promptConfig.requiredPlaceholders || promptConfig.required_placeholders).map((placeholder) => (
                          <span
                            key={placeholder}
                            className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded"
                          >
                            {placeholder}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Enable to edit checkbox - Bottom right */}
                    <div className="mt-3 flex justify-end">
                      <div 
                        className="flex items-center gap-2"
                        onClick={(e) => e.stopPropagation()} // Prevent expand when clicking checkbox
                      >
                        <input
                          type="checkbox"
                          checked={isEnabled}
                          onChange={() => handlePromptToggle(promptConfig.key)}
                          className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                        <span className="text-xs font-medium text-gray-700 whitespace-nowrap">
                          Enable to edit
                        </span>
                      </div>
                    </div>

                    {/* Validation Errors */}
                    {errors.length > 0 && (
                      <div className="mt-2 flex items-start gap-2 text-xs text-red-600">
                        <ExclamationCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>
                          {errors.map((error, idx) => (
                            <div key={idx}>{error}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Prompt Content (Collapsible) */}
                  {isExpanded && (
                    <div className="p-4 bg-white border-t border-gray-200">
                      <div className="space-y-3">
                        {isEnabled ? (
                          <>
                            <textarea
                              value={promptText}
                              onChange={(e) => handlePromptChange(promptConfig.key, e.target.value)}
                              rows={12}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Enter custom prompt text..."
                            />
                            <div className="flex justify-end">
                              <button
                                onClick={() => handleResetPrompt(promptConfig.key)}
                                disabled={!defaultPromptsToUse[promptConfig.key]}
                                className="text-xs text-gray-600 hover:text-gray-900 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-1"
                              >
                                <ArrowPathIcon className="h-3 w-3" />
                                Reset to Default
                              </button>
                            </div>
                          </>
                        ) : (
                          <>
                            <textarea
                              value={promptText}
                              readOnly
                              rows={12}
                              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm font-mono bg-gray-50 text-gray-700 cursor-not-allowed"
                              placeholder="Enable checkbox above to edit this prompt"
                            />
                            <div className="text-xs text-gray-500 italic flex items-center gap-1">
                              <ExclamationCircleIcon className="h-4 w-4" />
                              Check the "Enable to edit" checkbox above to customize this prompt. The default prompt will be used otherwise.
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Submit Error */}
        {submitError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <ExclamationCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-800 whitespace-pre-line">
                {submitError}
              </div>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isFormValid() || isValidating}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isValidating ? 'Validating...' : 'Generate Report'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ReportConfigModal;

